import { useEffect } from 'react';

interface ShoppingGuideModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function ShoppingGuideModal({ isOpen, onClose }: ShoppingGuideModalProps) {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      style={{ backgroundColor: 'rgba(0,0,0,0.6)' }}
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl w-full max-w-lg mx-4 overflow-hidden shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="bg-[#F7F4F0] px-8 pt-8 pb-6 relative">
          <button
            onClick={onClose}
            className="absolute top-5 right-5 w-8 h-8 flex items-center justify-center rounded-full bg-black/10 hover:bg-black/20 transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-[#3D2B1F] text-lg"></i>
          </button>
          <p className="text-xs font-medium tracking-widest text-[#8B7355] uppercase mb-2">Shopping Guide</p>
          <h2 className="text-2xl font-bold text-[#3D2B1F]">구매 안내</h2>
          <p className="text-xs text-[#8B7355] mt-2">위딘 온라인 스토어 이용 가이드</p>
        </div>

        {/* Body */}
        <div className="px-8 py-7 max-h-[60vh] overflow-y-auto space-y-6">

          {/* 1. 주문 방법 */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <div className="w-6 h-6 flex items-center justify-center">
                <i className="ri-shopping-cart-2-line text-[#8B7355] text-lg"></i>
              </div>
              <h3 className="text-base font-semibold text-[#3D2B1F]">1. 주문 방법</h3>
            </div>
            <div className="pl-8 space-y-2">
              {[
                { step: '01', text: '원하는 상품 페이지에서 옵션(용량·타입) 선택' },
                { step: '02', text: '수량 선택 후 [구매하기] 또는 [장바구니] 클릭' },
                { step: '03', text: '배송지 정보 입력 및 결제 수단 선택' },
                { step: '04', text: '최종 주문 내용 확인 후 [결제하기] 클릭' },
                { step: '05', text: '결제 완료 후 주문 확인 이메일 수신' },
              ].map(({ step, text }) => (
                <div key={step} className="flex items-center gap-3 bg-[#F7F4F0] rounded-lg px-4 py-3">
                  <span className="text-xs font-bold text-[#8B7355] w-5 flex-shrink-0">{step}</span>
                  <span className="text-sm text-[#5C4A3A] leading-snug">{text}</span>
                </div>
              ))}
            </div>
            <div className="pl-8 mt-3 flex items-start gap-2 bg-[#8B7355]/10 rounded-lg px-4 py-3">
              <i className="ri-information-line text-[#8B7355] text-base mt-0.5 flex-shrink-0"></i>
              <p className="text-xs text-[#8B7355] leading-relaxed">
                회원 및 비회원 모두 구매 가능합니다. 회원으로 가입하시면 주문 내역 조회, 적립금 적립 등 추가 혜택을 받으실 수 있습니다.
              </p>
            </div>
          </div>

          <div className="border-t border-[#EDE8E0]"></div>

          {/* 2. 결제 수단 */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <div className="w-6 h-6 flex items-center justify-center">
                <i className="ri-bank-card-line text-[#8B7355] text-lg"></i>
              </div>
              <h3 className="text-base font-semibold text-[#3D2B1F]">2. 결제 수단</h3>
            </div>
            <div className="pl-8 space-y-2">
              {[
                { icon: 'ri-bank-card-line', label: '신용카드 / 체크카드', desc: '국내외 모든 카드사 지원' },
                { icon: 'ri-kakao-talk-line', label: '카카오페이 / 네이버페이', desc: '간편 결제 서비스' },
                { icon: 'ri-exchange-line', label: '실시간 계좌이체', desc: '주요 시중은행 지원' },
                { icon: 'ri-money-cny-box-line', label: '무통장 입금 (가상계좌)', desc: '입금 확인 후 주문 확정' },
                { icon: 'ri-gift-line', label: '적립금 / 쿠폰', desc: '다른 결제 수단과 병용 가능' },
              ].map(({ icon, label, desc }) => (
                <div key={label} className="flex items-center gap-3 bg-[#F7F4F0] rounded-lg px-4 py-3">
                  <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                    <i className={`${icon} text-[#8B7355] text-base`}></i>
                  </div>
                  <div>
                    <p className="text-sm text-[#3D2B1F] font-medium leading-snug">{label}</p>
                    <p className="text-xs text-[#8B7355]">{desc}</p>
                  </div>
                </div>
              ))}
            </div>
            <div className="pl-8 mt-3 flex items-start gap-2 bg-[#8B7355]/10 rounded-lg px-4 py-3">
              <i className="ri-shield-check-line text-[#8B7355] text-base mt-0.5 flex-shrink-0"></i>
              <p className="text-xs text-[#8B7355] leading-relaxed">
                모든 결제는 SSL 보안 프로토콜로 암호화되어 처리됩니다. 결제 정보는 당사 서버에 저장되지 않습니다.
              </p>
            </div>
          </div>

          <div className="border-t border-[#EDE8E0]"></div>

          {/* 3. 주문 확인 및 취소 */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <div className="w-6 h-6 flex items-center justify-center">
                <i className="ri-file-list-3-line text-[#8B7355] text-lg"></i>
              </div>
              <h3 className="text-base font-semibold text-[#3D2B1F]">3. 주문 확인 및 취소</h3>
            </div>
            <div className="pl-8 space-y-3 text-sm text-[#5C4A3A] leading-relaxed">
              <p>결제 완료 후 등록된 이메일로 <strong className="text-[#3D2B1F]">주문 확인 메일</strong>이 발송됩니다. 메일이 수신되지 않을 경우 스팸함을 확인하거나 고객센터로 문의해 주세요.</p>
              <p>주문 취소는 <strong className="text-[#3D2B1F]">출고 전</strong>에만 가능합니다. 출고 이후에는 취소 대신 반품 절차로 진행됩니다.</p>
              <div>
                <p className="font-semibold text-[#3D2B1F] mb-1">무통장 입금 주의사항</p>
                <p>가상계좌 발급 후 <strong className="text-[#3D2B1F]">24시간 이내</strong> 미입금 시 주문이 자동 취소됩니다. 입금 시 반드시 주문자 성함으로 입금해 주세요.</p>
              </div>
            </div>
          </div>

          <div className="border-t border-[#EDE8E0]"></div>

          {/* 4. 적립금 · 쿠폰 */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <div className="w-6 h-6 flex items-center justify-center">
                <i className="ri-coupon-3-line text-[#8B7355] text-lg"></i>
              </div>
              <h3 className="text-base font-semibold text-[#3D2B1F]">4. 적립금 · 쿠폰</h3>
            </div>
            <div className="pl-8 space-y-2">
              {[
                { label: '적립 기준', value: '결제 금액의 1% 자동 적립' },
                { label: '적립 확정', value: '구매 확정 후 7일 이내' },
                { label: '최소 사용 금액', value: '1,000원 이상 보유 시 사용 가능' },
                { label: '유효 기간', value: '적립일로부터 1년' },
              ].map(({ label, value }) => (
                <div key={label} className="flex items-center justify-between bg-[#F7F4F0] rounded-lg px-4 py-3">
                  <span className="text-sm text-[#5C4A3A]">{label}</span>
                  <span className="text-sm font-semibold text-[#8B7355]">{value}</span>
                </div>
              ))}
            </div>
            <p className="pl-8 mt-3 text-xs text-[#8B7355]">
              쿠폰은 결제 화면에서 직접 적용할 수 있으며, 1회 주문당 1장만 사용 가능합니다. 적립금과 쿠폰은 중복 적용할 수 없습니다.
            </p>
          </div>

          <div className="border-t border-[#EDE8E0]"></div>

          {/* 5. 배송 · 교환 · 환불 요약 */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <div className="w-6 h-6 flex items-center justify-center">
                <i className="ri-truck-line text-[#8B7355] text-lg"></i>
              </div>
              <h3 className="text-base font-semibold text-[#3D2B1F]">5. 배송 · 교환 · 환불 요약</h3>
            </div>
            <div className="pl-8 space-y-3 text-sm text-[#5C4A3A] leading-relaxed">
              <div className="flex items-start gap-3">
                <div className="w-5 h-5 flex items-center justify-center mt-0.5 flex-shrink-0">
                  <i className="ri-truck-line text-[#8B7355] text-base"></i>
                </div>
                <div>
                  <p className="font-semibold text-[#3D2B1F] mb-0.5">배송</p>
                  <p>결제 확정 후 1–3 영업일 내 출고 / 5만 원 이상 무료 배송</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-5 h-5 flex items-center justify-center mt-0.5 flex-shrink-0">
                  <i className="ri-exchange-line text-[#8B7355] text-base"></i>
                </div>
                <div>
                  <p className="font-semibold text-[#3D2B1F] mb-0.5">교환 · 환불</p>
                  <p>수령 후 7일 이내 단순 변심 / 30일 이내 상품 불량·오배송 접수 가능</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-5 h-5 flex items-center justify-center mt-0.5 flex-shrink-0">
                  <i className="ri-alert-line text-[#8B7355] text-base"></i>
                </div>
                <div>
                  <p className="font-semibold text-[#3D2B1F] mb-0.5">교환 · 환불 불가</p>
                  <p>개봉 후 사용·훼손된 상품, 화장품류 위생 문제 등은 반품이 제한될 수 있습니다.</p>
                </div>
              </div>
            </div>
            <p className="pl-8 mt-3 text-xs text-[#8B7355]">
              자세한 내용은 푸터의 <strong>환불 및 반품 정책</strong>과 <strong>배송 정책</strong>을 참고해 주세요.
            </p>
          </div>

          <div className="border-t border-[#EDE8E0]"></div>

          {/* 6. 고객센터 */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <div className="w-6 h-6 flex items-center justify-center">
                <i className="ri-customer-service-2-line text-[#8B7355] text-lg"></i>
              </div>
              <h3 className="text-base font-semibold text-[#3D2B1F]">6. 고객센터</h3>
            </div>
            <div className="pl-8 space-y-2">
              {[
                { label: '이메일', value: 'zeowithin@gmail.com' },
                { label: '운영 시간', value: '평일 10:00 – 17:00' },
                { label: '점심 시간', value: '12:00 – 13:00 (제외)' },
              ].map(({ label, value }) => (
                <div key={label} className="flex items-center justify-between bg-[#F7F4F0] rounded-lg px-4 py-3">
                  <span className="text-sm text-[#5C4A3A]">{label}</span>
                  <span className="text-sm font-semibold text-[#8B7355]">{value}</span>
                </div>
              ))}
            </div>
            <p className="pl-8 mt-3 text-xs text-[#8B7355]">
              주말·공휴일 접수된 문의는 다음 영업일에 순차적으로 답변 드립니다.
            </p>
          </div>

        </div>

        {/* Footer */}
        <div className="px-8 py-5 border-t border-[#EDE8E0]">
          <button
            onClick={onClose}
            className="w-full bg-[#8B7355] text-white text-sm font-medium py-3 rounded-xl hover:bg-[#7A6245] transition-colors duration-200 cursor-pointer whitespace-nowrap"
          >
            확인
          </button>
        </div>
      </div>
    </div>
  );
}
